import React from "react";

// UI components
import { Col, Row } from "react-bootstrap";
import { Typography } from "@mui/material";

/**
 * The "Latest updates" row on a room dashboard: one card per other tab, each
 * listing the most recent things that happened there.
 *
 * Shared by the buyer portal and the seller's editor so the two can't drift
 * apart - the editor is meant to be a faithful preview, and this markup is
 * the only copy of it. Only the data differs: the buyer builds cards from the
 * live activity feed and thread, the editor from the room being edited.
 *
 * @param {Array} cards
 *   Cards to render, already filtered to the visible ones. Each is
 *   {key, label, lines: [{key, title, meta}], fallback}. `lines` empty falls
 *   back to the `fallback` sentence.
 * @param {string} owner
 *   Typography owner - "client" in the portal, "admin" in the editor.
 * @param {Function|null} onOpen
 *   Called with the card when it's activated. Omit for a static preview: the
 *   card then renders as a plain div rather than a button.
 */
export default function RoomUpdateCards({
  cards,
  owner = "client",
  onOpen = null,
}) {
  if (!cards || cards.length === 0) {
    return null;
  }

  // Cards share the row evenly, however many there are: 3 -> 4 columns each,
  // 2 -> 6, 1 -> full width.
  const span = Math.floor(12 / cards.length);
  const Card = onOpen ? "button" : "div";

  return (
    <section className="buyer-room__updates mb-5">
      <Typography
        owner={owner}
        variant="h5"
        className="buyer-room__section-title"
        family="secondary"
      >
        Latest updates
      </Typography>
      <Row className="g-4 align-items-stretch">
        {cards.map((card) => (
          <Col xs={12} md={span} className="d-flex" key={card.key}>
            {/* When interactive, the whole card opens its tab - these are a
                shortcut into the section, not a separate place to read. */}
            <Card
              {...(onOpen
                ? { type: "button", onClick: () => onOpen(card) }
                : {})}
              className={`buyer-room__update-card ${
                onOpen ? "" : "buyer-room__update-card--static"
              }`}
            >
              <Typography
                owner={owner}
                variant="h6"
                className="buyer-room__update-title"
              >
                {card.label}
              </Typography>
              {card.lines.length > 0 ? (
                <ul className="buyer-room__update-list">
                  {card.lines.map((line) => (
                    <li key={line.key} title={line.title}>
                      <Typography
                        owner={owner}
                        variant="body2"
                        className="buyer-room__update-line"
                      >
                        {line.title}
                      </Typography>
                      <Typography
                        owner={owner}
                        variant="caption"
                        className="buyer-room__update-meta"
                      >
                        {line.meta}
                      </Typography>
                    </li>
                  ))}
                </ul>
              ) : (
                <Typography
                  owner={owner}
                  variant="body2"
                  className="buyer-room__update-empty"
                >
                  {card.fallback}
                </Typography>
              )}
            </Card>
          </Col>
        ))}
      </Row>
    </section>
  );
}
